import { useRef, useEffect, useState } from "react";
import { AiOutlineArrowLeft } from "react-icons/ai";
import CustomSvg from "../svgs/CustomSvg";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { toast } from "react-toastify";

const GAME_URL = process.env.REACT_APP_GAME_SERVER_URL;

const TournamentLobby = () => {
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));
  const { tournamentId, gameId, gameName, lobbyCode } = useParams();
  const [tournamentEnded, setTournamentEnded] = useState(false);
  const [matchedLobbyCode, setMatchedLobbyCode] = useState(null);
  const [status, setStatus] = useState(
    "Please wait while we set up your match."
  );
  const [opponent, setOpponent] = useState({
    name: "Random",
    avatar: "https://ui-avatars.com/api/?name=Random",
  });

  const socketRef = useRef(null);
  const timeoutRef = useRef(null);
  const matchedLobbyCodeRef = useRef(null);

  useEffect(() => {
    matchedLobbyCodeRef.current = matchedLobbyCode;
  }, [matchedLobbyCode]);

  const initializeSocket = () => {
    // Disconnect and clean previous socket
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Clear timeout if any
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const socket = io(`${GAME_URL}/tournament`, {
      autoConnect: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      pingInterval: 25000,
      pingTimeout: 30000,
      query: {
        userId: user._id,
        tournamentId,
        isOwner: false,
      },
      auth: {
        userId: user._id,
        tournamentId,
        isOwner: false,
      },
    });

    socketRef.current = socket;

    timeoutRef.current = setTimeout(() => {
      if (!matchedLobbyCodeRef.current) {
        console.warn("No match found after 20s. Retrying...");
        setStatus("Retrying matchmaking...");

        socket.removeAllListeners();
        socket.disconnect();
        socketRef.current = null;

        setTimeout(() => {
          initializeSocket();
        }, 500); // Retry after short delay
      }
    }, 20000);

    socket.on("connect", () => {
      console.log("Socket connected!");
    });

    socket.on("timed-out", () => {
      console.warn("Match timed out: opponent didn't join in time.");

      if (window.gameWindow && !window.gameWindow.closed) {
        window.gameWindow.close();
      }
      window.gameWindow = null;

      if (matchedLobbyCode) {
        setMatchedLobbyCode(null);
      }

      setStatus("Match timed out. Opponent didn't join.");

      toast.info("⌛ Match did not hold — opponent didn’t join in time.");
    });

    socket.on("connect_error", (err) => {
      console.error("Socket connect_error:", err);
      setStatus("Connection error. Retrying...");
    });

    socket.on("disconnect", (reason) => {
      console.warn("Socket disconnected:", reason);
    });

    socket.on("no-more-fixtures", () => {
      toast.info("✅ You have completed all your fixtures.");
      setTournamentEnded(true);
    });

    socket.on("matched", (data) => {
      console.log("Matched:", data);
      clearTimeout(timeoutRef.current);
      setMatchedLobbyCode(data.lobbyCode);
      setStatus("Match found! You have 60s to start the game.");
      const opp =
        data?.userOneGameDetails?.userId === user?._id
          ? data?.userTwoGameDetails
          : data?.userOneGameDetails;

      setOpponent({
        name: opp?.username,
        avatar: opp?.avatar,
      });
      socket.emit("receive", {
        lobbyCode: data?.lobbyCode,
        userId: user?._id,
      });
    });

    socket.on("error", (errorData) => {
      console.error("Socket error:", errorData);
      toast.error(` ${errorData?.message}`);
    });

    socket.on("gameEnd", (result) => {
      console.log("Game ended with result:", result);

      const gameEndResult = {
        ...result,
        playerId: user._id,
        stakeAmount: 0,
      };

      localStorage.setItem("gameResult", JSON.stringify(gameEndResult));

      socket.emit("resultReceived", {
        gameId: gameId || "unknown",
        playerId: user._id,
        result: gameEndResult,
      });

      if (window.gameWindow && !window.gameWindow.closed) {
        window.gameWindow.close();
      }
      window.gameWindow = null;
      setMatchedLobbyCode(null);

      if (result.winner === user._id) {
        toast.success("🎉 You won! Get ready for the next match");
      } else if (result.loser === user._id) {
        toast.error("😞 You lost! Get ready for the next match");
      } else {
        toast("Match ended! Get ready for the next match");
      }

      navigate(
        `/tournament/lobby/${lobbyCode}/${tournamentId}/${gameId}/${gameName}`
      );
    });
  };

  useEffect(() => {
    if (!user) {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) window.location.reload();
      return;
    }

    initializeSocket();

    return () => {
      clearTimeout(timeoutRef.current);

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      if (window.gameWindow && !window.gameWindow.closed) {
        window.gameWindow.close();
      }
      window.gameWindow = null;

      console.log("Socket cleanup complete.");
    };
  }, [user, tournamentId, gameId, gameName, lobbyCode, navigate]);

  const handleNextGame = () => {
    const url = `${GAME_URL}/game?gameId=${gameId}&tournamentId=${tournamentId}&playerId=${user._id}&opponentId=null&gameName=${gameName}&lobbyId=null&lobbyCode=${matchedLobbyCode}&stakeAmount=null`;
    window.gameWindow = window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#130828] px-4 text-white">
      <div className="relative flex items-center justify-center py-4">
        <div className="absolute -left-1">
          <button
            onClick={() => navigate("/tournaments")}
            className="flex items-center gap-2 text-white"
          >
            <CustomSvg name="arrow-left" width={20} height={20} color="#fff" />
            <AiOutlineArrowLeft size={24} />
          </button>
        </div>
        <h2 className="md:text-xl text-sm font-bold text-center">
          Tournaments{" "}
          <span className="bg-gradient-to-r from-[#C6BCFF] to-[#ec4899] bg-clip-text text-transparent">
            Waiting Room
          </span>
        </h2>
      </div>

      <div className="flex flex-col items-center justify-between w-full max-w-md gap-4 px-6 rounded-[30px] relative">
        {/* User Card */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src={user?.avatar || "https://ui-avatars.com/api/?name=You"}
              alt="Your Avatar"
              className="w-32 p-3 border-8 border-[#E9FFB7] h-32 rounded-full bg-[#FFB1E7]"
            />
            <span className="absolute bottom-12 -left-1 -translate-x-1/2 bg-[#1DA1F2] text-white text-xs px-6 py-1 rounded-full">
              You
            </span>
          </div>
          <p className="text-white font-medium mt-2">
            {user?.firstName || "You"}
          </p>
        </div>

        {/* VS */}
        <p className="text-white font-bold text-4xl">VS</p>

        {/* Opponent */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src={opponent.avatar}
              alt="Opponent Avatar"
              className="w-32 p-3 border-8 border-[#E9FFB7] h-32 rounded-full bg-[#9FD1D7]"
            />
            <span className="absolute text-xs bottom-12 -right-32 -translate-x-1/2 bg-[#1DA1F2] text-white px-6 py-1 rounded-full">
              Opponent
            </span>
          </div>
          <p className="text-white font-medium mt-2">{opponent.name}</p>
        </div>

        {/* Message and Button */}
        <div className="my-2 text-center">
          <p className="text-sm mb-4">{status}</p>
          <button
            disabled={!matchedLobbyCode || tournamentEnded}
            onClick={handleNextGame}
            className={`px-3 py-2 text-sm rounded-full font-bold text-white transition ${
              tournamentEnded
                ? "bg-gray-500 cursor-not-allowed"
                : matchedLobbyCode
                ? "bg-pink-500 hover:bg-pink-600"
                : "bg-gray-500 cursor-not-allowed"
            }`}
          >
            {tournamentEnded
              ? "Tournament Ended"
              : matchedLobbyCode
              ? "Play Game"
              : "Waiting for match..."}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentLobby;
